import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { matchingAPI } from '../../services/api';
import {
  Building2,
  MapPin,
  Loader2,
  Search,
  TrendingUp,
  Star,
  Filter,
  Target,
  Zap,
  ChevronRight,
  ArrowUpRight,
  Sparkles,
  LayoutGrid,
  AlertCircle,
  RefreshCw,
  FlaskConical,
  Eye,
} from 'lucide-react';

// Match quality badge component
const MatchQualityBadge = ({ quality, score }) => {
  const styles = {
    excellent: 'bg-green-100 text-green-700 border-green-200',
    strong: 'bg-blue-100 text-blue-700 border-blue-200',
    moderate: 'bg-amber-100 text-amber-700 border-amber-200',
    weak: 'bg-orange-100 text-orange-700 border-orange-200',
    poor: 'bg-gray-100 text-gray-600 border-gray-200',
  };

  const labels = {
    excellent: 'Excellent Match',
    strong: 'Strong Match',
    moderate: 'Moderate Match',
    weak: 'Weak Match',
    poor: 'Low Match',
  };

  return (
    <div className="flex items-center gap-2">
      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${styles[quality] || styles.poor}`}>
        {labels[quality] || 'Match'}
      </span>
      <span className="text-sm font-bold text-gray-900">{score}%</span>
    </div>
  );
};

// Stage indicator component
const StageIndicator = ({ stage }) => {
  const stageColors = {
    'Discovery': 'bg-blue-500',
    'Preclinical': 'bg-indigo-500',
    'Phase 1': 'bg-purple-500',
    'Phase 2': 'bg-pink-500',
    'Phase 3': 'bg-orange-500',
    'Approved': 'bg-green-500',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full text-white ${stageColors[stage] || 'bg-gray-500'}`}>
      <span className="w-1.5 h-1.5 bg-white/50 rounded-full"></span>
      {stage}
    </span>
  );
};

// Seller match card component
function SellerMatchCard({ match }) {
  const reasons = Array.isArray(match.reasons) ? match.reasons : [];

  return (
    <Link
      to={`/buyer/company/${match.seller_id}`}
      className="block bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg hover:border-blue-200 transition-all group"
    >
      <div className="flex items-start gap-4">
        {/* Company Avatar */}
        <div className="flex-shrink-0">
          {match.logo_url ? (
            <img
              src={match.logo_url}
              alt={match.company_name}
              className="w-14 h-14 rounded-xl object-cover border border-gray-200"
            />
          ) : (
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
              {match.company_name?.substring(0, 2).toUpperCase() || 'CO'}
            </div>
          )}
        </div>

        {/* Company Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-2">
            <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
              {match.company_name}
              {match.company_name_cn && (
                <span className="text-gray-500 font-normal ml-2">({match.company_name_cn})</span>
              )}
            </h3>
            <MatchQualityBadge quality={match.match_quality} score={match.score} />
          </div>

          {/* Location and Stage */}
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mb-3">
            {match.headquarters && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {match.headquarters}
              </span>
            )}
            {(match.lead_asset_stage || match.stage) && (
              <StageIndicator stage={match.lead_asset_stage || match.stage} />
            )}
          </div>

          {/* Description */}
          {match.description && (
            <p className="text-sm text-gray-600 line-clamp-2 mb-3">
              {match.description}
            </p>
          )}

          {/* Match Reasons */}
          {reasons.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {reasons.slice(0, 3).map((reason, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-lg"
                >
                  <Sparkles className="w-3 h-3" />
                  {reason.replace(/^[^:]+:\s*/, '').substring(0, 40)}
                </span>
              ))}
            </div>
          )}

          {/* Therapeutic Areas */}
          {match.therapeutic_areas?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {match.therapeutic_areas.slice(0, 4).map((area, idx) => (
                <span
                  key={idx}
                  className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full"
                >
                  {area}
                </span>
              ))}
              {match.therapeutic_areas.length > 4 && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                  +{match.therapeutic_areas.length - 4} more
                </span>
              )}
            </div>
          )}
        </div>

        {/* Arrow */}
        <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowUpRight className="w-5 h-5 text-blue-600" />
        </div>
      </div>
    </Link>
  );
}

export default function BuyerMatches() {
  const { userProfile } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, excellent, strong, moderate
  const [buyerProfile, setBuyerProfile] = useState(null);

  useEffect(() => {
    loadMatches();
  }, []);

  const loadMatches = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await matchingAPI.getBuyerRecommendations();
      if (response.data?.success) {
        setMatches(response.data.data?.recommendations || []);
        setBuyerProfile(response.data.data?.buyer_profile || null);
      } else {
        setError(response.data?.message || 'Failed to load matches');
      }
    } catch (err) {
      console.error('Failed to load matches:', err);
      setError(err.response?.data?.error || 'Failed to load matches. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter matches
  const filteredMatches = useMemo(() => {
    if (filter === 'all') return matches;
    return matches.filter(m => m.match_quality === filter);
  }, [matches, filter]);

  // Stats
  const stats = useMemo(() => ({
    total: matches.length,
    excellent: matches.filter(m => m.match_quality === 'excellent').length,
    strong: matches.filter(m => m.match_quality === 'strong').length,
    moderate: matches.filter(m => m.match_quality === 'moderate').length,
  }), [matches]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Finding companies that match your criteria...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Sparkles className="w-7 h-7 text-blue-600" />
            Smart Matches
          </h1>
          <p className="text-gray-500 mt-1">
            Companies matching your therapeutic areas, platforms, and BD focus
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadMatches}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <Link
            to="/buyer/search"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <Search className="w-4 h-4" />
            Browse All
          </Link>
        </div>
      </div>

      {/* Your Matching Criteria Card */}
      {buyerProfile && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-5">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
                <Target className="w-5 h-5 text-blue-600" />
                Your Matching Criteria
              </h2>
              <div className="flex flex-wrap gap-4">
                {buyerProfile.therapeutic_areas?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Therapeutic Areas</p>
                    <div className="flex flex-wrap gap-1">
                      {buyerProfile.therapeutic_areas.slice(0, 5).map((area, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-white text-purple-700 text-xs rounded-full border border-purple-200">
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {buyerProfile.tech_platforms?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Platforms</p>
                    <div className="flex flex-wrap gap-1">
                      {buyerProfile.tech_platforms.slice(0, 5).map((plat, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-white text-blue-700 text-xs rounded-full border border-blue-200">
                          {plat}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <Link
              to="/buyer/profile"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              Edit Profile
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <Building2 className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-xs text-gray-500">Total Matches</div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <Star className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-2xl font-bold text-green-600">{stats.excellent}</div>
          <div className="text-xs text-gray-500">Excellent Matches</div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-blue-600">{stats.strong}</div>
          <div className="text-xs text-gray-500">Strong Matches</div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <Zap className="w-5 h-5 text-amber-600" />
          </div>
          <div className="text-2xl font-bold text-amber-600">{stats.moderate}</div>
          <div className="text-xs text-gray-500">Moderate Matches</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-blue-600" />
            Matched Companies
          </h2>
          <div className="text-sm text-gray-500">
            {filteredMatches.length} companies
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { key: 'all', label: 'All Matches', icon: LayoutGrid },
            { key: 'excellent', label: 'Excellent', icon: Star },
            { key: 'strong', label: 'Strong', icon: TrendingUp },
            { key: 'moderate', label: 'Moderate', icon: Zap },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filter === f.key
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
              }`}
            >
              <f.icon className="w-3.5 h-3.5" />
              {f.label}
              {f.key === 'all' && ` (${stats.total})`}
              {f.key === 'excellent' && stats.excellent > 0 && ` (${stats.excellent})`}
              {f.key === 'strong' && stats.strong > 0 && ` (${stats.strong})`}
              {f.key === 'moderate' && stats.moderate > 0 && ` (${stats.moderate})`}
            </button>
          ))}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">{error}</p>
              <button
                onClick={loadMatches}
                className="text-sm text-red-600 hover:text-red-700 font-medium mt-1"
              >
                Try again
              </button>
            </div>
          </div>
        )}

        {/* Matches List */}
        {!error && filteredMatches.length > 0 && (
          <div className="space-y-4">
            {filteredMatches.map(match => (
              <SellerMatchCard key={match.seller_id} match={match} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!error && filteredMatches.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'all' ? 'No matches found' : `No ${filter} matches`}
            </h3>
            <p className="text-gray-500 mb-4">
              {filter === 'all'
                ? 'Update your profile with therapeutic areas and platforms to find matching companies'
                : 'Try adjusting your filter to see more matches'}
            </p>
            <div className="flex items-center justify-center gap-3">
              <Link
                to="/buyer/profile"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Target className="w-4 h-4" />
                Update Profile
              </Link>
              <Link
                to="/buyer/search"
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Search className="w-4 h-4" />
                Browse All Companies
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
